import _ from "lodash";
import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { withTranslation, WithTranslation } from "react-i18next";
import MediaQuery from "react-responsive";

import {
  H2,
  H2Pre,
  Body1Pre,
  buttonCSS,
  H4,
  Button,
} from "src/components/Typography";
import { colors } from "src/styles/colors";
import images from "src/images";
import { translate } from "src/locales";
import { media, mobileWidth, desktopWidth } from "src/utils/media";

type Props = WithTranslation;

const Container = styled.div`
  display: flex;
  background-color: ${colors.bg.white.main};
  min-width: 1440px;
  padding-top: 200px;
  padding-bottom: 200px;
  ${media.mobile`
    min-width: auto;
    height: auto;
    padding-top: 108px;
    padding-bottom: 0px;
  `}
  ${media.desktop`
    padding-bottom: 136px;
  `}
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  width: 1268px;
  margin: auto;
  ${media.mobile`
    flex-direction: column;
    width: 328px;
  `}
`;

const Title = styled(H2Pre)<{ $isKo: boolean }>`
  width: 100%;
  text-align: center;
  color: ${colors.text.dark.title};
  margin-bottom: 24px;
  ${media.desktop`
    white-space: normal;
  `}
  ${media.mobile`
    margin-bottom: 16px;
  `}
  ${({ $isKo }) =>
    $isKo
      ? css`
          ${media.mobile`
width: 230px;
margin-left: auto;
margin-right: auto;
  `}
        `
      : css``}
`;

const Description = styled(Body1Pre)`
  text-align: center;
  color: ${colors.text.dark.body};
  ${media.mobile`
    padding-left: 24px;
    padding-right: 24px;
  `}
`;

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding-top: 80px;
  ${media.mobile`
    padding-top: 40px;
  `}
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  ${media.mobile`
    flex-wrap: wrap;
    & div:nth-child(2n + 1) {
      margin-right: 8px;
    }
    & div:last-child {
      margin-right: 0px;
    }
  `}
  ${media.desktop`
    & div + div {
      margin-left: 32px;
    }
  `}
`;

const CustomerItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  width: 294px;
  height: 148px;
  background-color: ${colors.bg.white.sub};
  margin-bottom: 32px;
  ${media.mobile`
    width: 160px;
    height: 80px;
    margin-bottom: 8px;
  `}
`;

const CustomerLogo = styled.img`
  width: 214px;
  height: 63px;
  ${media.mobile`
    width: 116px;
    height: 34px;
  `}
`;

const MoreOverlay = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: calc(100% - 150px);
  background: linear-gradient(
    0deg,
    #ffffff 0%,
    #ffffff 0%,
    rgba(255, 255, 255, 0) 35%
  );
`;

const MoreEmpty = styled.div`
  width: 100%;
  height: 96px;
`;

const MoreGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-top: 33px;
  margin-bottom: 96px;
`;

const MoreButton = styled(Button)`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${colors.bg.black.main};
  cursor: pointer;
`;

const MoreIcon = styled.img`
  width: 20px;
  height: 20px;
`;

type LogoType =
  | "11ss"
  | "bitberry"
  | "carry"
  | "danal"
  | "flybit"
  | "foblgate"
  | "hashed"
  | "kbbank"
  | "koda"
  | "sandbank"
  | "shinhan"
  | "zikto"
  | "1inch"
  | "badger"
  | "bithumb"
  | "klaytn"
  | "lg"
  | "skSecurities"
  | "sushiSwap"
  | "skTelecom";

const renderLogoByLanguageAndType = (language: string, type: LogoType) => {
  const byLanguage: Record<
    LogoType,
    { KoLogo: React.ReactNode; EnLogo: React.ReactNode }
  > = {
    skTelecom: {
      KoLogo: (
        <CustomerLogo
          src={images.customerSKTelecomLogoSrc}
          srcSet={images.customerSKTelecomLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerSKTelecomLogoSrc}
          srcSet={images.customerSKTelecomLogo}
        />
      ),
    },
    sushiSwap: {
      KoLogo: (
        <CustomerLogo
          src={images.customerSushiSwapLogoSrc}
          srcSet={images.customerSushiSwapLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerSushiSwapLogoSrc}
          srcSet={images.customerSushiSwapLogo}
        />
      ),
    },
    skSecurities: {
      KoLogo: (
        <CustomerLogo
          src={images.customerSKSecuritiesLogoSrc}
          srcSet={images.customerSKSecuritiesLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerSKSecuritiesEnLogoSrc}
          srcSet={images.customerSKSecuritiesEnLogo}
        />
      ),
    },
    lg: {
      KoLogo: (
        <CustomerLogo
          src={images.customerLGLogoSrc}
          srcSet={images.customerLGLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerLGLogoSrc}
          srcSet={images.customerLGLogo}
        />
      ),
    },
    klaytn: {
      KoLogo: (
        <CustomerLogo
          src={images.customerKlaytnLogoSrc}
          srcSet={images.customerKlaytnLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerKlaytnLogoSrc}
          srcSet={images.customerKlaytnLogo}
        />
      ),
    },
    bithumb: {
      KoLogo: (
        <CustomerLogo
          src={images.customerBithumbLogoSrc}
          srcSet={images.customerBithumbLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerBithumbLogoSrc}
          srcSet={images.customerBithumbLogo}
        />
      ),
    },
    badger: {
      KoLogo: (
        <CustomerLogo
          src={images.customerBadgerLogoSrc}
          srcSet={images.customerBadgerLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerBadgerLogoSrc}
          srcSet={images.customerBadgerLogo}
        />
      ),
    },
    ["1inch"]: {
      KoLogo: (
        <CustomerLogo
          src={images.customer1inchLogoSrc}
          srcSet={images.customer1inchLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customer1inchLogoSrc}
          srcSet={images.customer1inchLogo}
        />
      ),
    },
    ["11ss"]: {
      KoLogo: (
        <CustomerLogo
          src={images.customer11ssLogoSrc}
          srcSet={images.customer11ssLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customer11ssLogoSrc}
          srcSet={images.customer11ssLogo}
        />
      ),
    },
    bitberry: {
      KoLogo: (
        <CustomerLogo
          src={images.customerBitberryLogoSrc}
          srcSet={images.customerBitberryLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerBitberryLogoSrc}
          srcSet={images.customerBitberryLogo}
        />
      ),
    },
    carry: {
      KoLogo: (
        <CustomerLogo
          src={images.customerCarryLogoSrc}
          srcSet={images.customerCarryLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerCarryLogoSrc}
          srcSet={images.customerCarryLogo}
        />
      ),
    },
    danal: {
      KoLogo: (
        <CustomerLogo
          src={images.customerDanalLogoSrc}
          srcSet={images.customerDanalLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerDanalLogoSrc}
          srcSet={images.customerDanalLogo}
        />
      ),
    },
    flybit: {
      KoLogo: (
        <CustomerLogo
          src={images.customerFlybitLogoSrc}
          srcSet={images.customerFlybitLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerFlybitLogoSrc}
          srcSet={images.customerFlybitLogo}
        />
      ),
    },
    foblgate: {
      KoLogo: (
        <CustomerLogo
          src={images.customerFoblgateLogoSrc}
          srcSet={images.customerFoblgateLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerFoblgateLogoSrc}
          srcSet={images.customerFoblgateLogo}
        />
      ),
    },
    hashed: {
      KoLogo: (
        <CustomerLogo
          src={images.customerHashedLogoSrc}
          srcSet={images.customerHashedLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerHashedLogoSrc}
          srcSet={images.customerHashedLogo}
        />
      ),
    },
    kbbank: {
      KoLogo: (
        <CustomerLogo
          src={images.customerKBBankLogoSrc}
          srcSet={images.customerKBBankLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerKBBankEnLogoSrc}
          srcSet={images.customerKBBankEnLogo}
        />
      ),
    },
    koda: {
      KoLogo: (
        <CustomerLogo
          src={images.customerKodaLogoSrc}
          srcSet={images.customerKodaLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerKodaLogoSrc}
          srcSet={images.customerKodaLogo}
        />
      ),
    },
    sandbank: {
      KoLogo: (
        <CustomerLogo
          src={images.customerSandbankLogoSrc}
          srcSet={images.customerSandbankLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerSandbankLogoSrc}
          srcSet={images.customerSandbankLogo}
        />
      ),
    },
    shinhan: {
      KoLogo: (
        <CustomerLogo
          src={images.customerShinhanBankLogoSrc}
          srcSet={images.customerShinhanBankLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerShinhanBankEnLogoSrc}
          srcSet={images.customerShinhanBankEnLogo}
        />
      ),
    },
    zikto: {
      KoLogo: (
        <CustomerLogo
          src={images.customerZiktoLogoSrc}
          srcSet={images.customerZiktoLogo}
        />
      ),
      EnLogo: (
        <CustomerLogo
          src={images.customerZiktoLogoSrc}
          srcSet={images.customerZiktoLogo}
        />
      ),
    },
  };
  if (language === "ko") {
    return byLanguage[type].KoLogo;
  }
  return byLanguage[type].EnLogo;
};

const CustomerSection = (props: Props) => {
  const { i18n, t } = props;
  const language = i18n.language;
  const [isMore, setMore] = useState(false);
  const isKo = i18n.language === "ko";

  const onMore = () => {
    setMore(true);
  };

  return (
    <Container>
      <Inner>
        <Title $isKo={isKo}>{translate(["customerSection", "title"], t)}</Title>
        <Description>
          {translate(["customerSection", "description"], t)}
        </Description>
        <Content>
          <Row>
            <CustomerItem>
              {renderLogoByLanguageAndType(language, "kbbank")}
            </CustomerItem>
            <CustomerItem>
              {renderLogoByLanguageAndType(language, "shinhan")}
            </CustomerItem>
            <CustomerItem>
              {renderLogoByLanguageAndType(language, "danal")}
            </CustomerItem>
            <CustomerItem>
              {renderLogoByLanguageAndType(language, "koda")}
            </CustomerItem>
          </Row>
          <Row>
            <CustomerItem>
              {renderLogoByLanguageAndType(language, "bithumb")}
            </CustomerItem>
            <CustomerItem>
              {renderLogoByLanguageAndType(language, "flybit")}
            </CustomerItem>
            <CustomerItem>
              {renderLogoByLanguageAndType(language, "foblgate")}
            </CustomerItem>
            <CustomerItem>
              {renderLogoByLanguageAndType(language, "bitberry")}
            </CustomerItem>
          </Row>
          <Row>
            <CustomerItem>
              {renderLogoByLanguageAndType(language, "sandbank")}
            </CustomerItem>
            <CustomerItem>
              {renderLogoByLanguageAndType(language, "hashed")}
            </CustomerItem>
            <MediaQuery minWidth={desktopWidth}>
              <CustomerItem>
                {renderLogoByLanguageAndType(language, "carry")}
              </CustomerItem>
              <CustomerItem>
                {renderLogoByLanguageAndType(language, "11ss")}
              </CustomerItem>
            </MediaQuery>
            <MediaQuery maxWidth={mobileWidth}>
              {isMore ? (
                <React.Fragment>
                  <CustomerItem>
                    {renderLogoByLanguageAndType(language, "carry")}
                  </CustomerItem>
                  <CustomerItem>
                    {renderLogoByLanguageAndType(language, "11ss")}
                  </CustomerItem>
                </React.Fragment>
              ) : null}
            </MediaQuery>
          </Row>
          <MediaQuery minWidth={desktopWidth}>
            <Row>
              <CustomerItem>
                {renderLogoByLanguageAndType(language, "zikto")}
              </CustomerItem>
              <CustomerItem>
                {renderLogoByLanguageAndType(language, "lg")}
              </CustomerItem>
              <CustomerItem>
                {renderLogoByLanguageAndType(language, "skSecurities")}
              </CustomerItem>
              <CustomerItem>
                {renderLogoByLanguageAndType(language, "skTelecom")}
              </CustomerItem>
            </Row>
            <Row>
              <CustomerItem>
                {renderLogoByLanguageAndType(language, "sushiSwap")}
              </CustomerItem>
              <CustomerItem>
                {renderLogoByLanguageAndType(language, "1inch")}
              </CustomerItem>
              <CustomerItem>
                {renderLogoByLanguageAndType(language, "klaytn")}
              </CustomerItem>
              <CustomerItem>
                {renderLogoByLanguageAndType(language, "badger")}
              </CustomerItem>
            </Row>
          </MediaQuery>
          <MediaQuery maxWidth={mobileWidth}>
            {isMore ? (
              <React.Fragment>
                <Row>
                  <CustomerItem>
                    {renderLogoByLanguageAndType(language, "zikto")}
                  </CustomerItem>
                  <CustomerItem>
                    {renderLogoByLanguageAndType(language, "lg")}
                  </CustomerItem>
                  <CustomerItem>
                    {renderLogoByLanguageAndType(language, "skSecurities")}
                  </CustomerItem>
                  <CustomerItem>
                    {renderLogoByLanguageAndType(language, "skTelecom")}
                  </CustomerItem>
                </Row>
                <Row>
                  <CustomerItem>
                    {renderLogoByLanguageAndType(language, "sushiSwap")}
                  </CustomerItem>
                  <CustomerItem>
                    {renderLogoByLanguageAndType(language, "1inch")}
                  </CustomerItem>
                  <CustomerItem>
                    {renderLogoByLanguageAndType(language, "klaytn")}
                  </CustomerItem>
                  <CustomerItem>
                    {renderLogoByLanguageAndType(language, "badger")}
                  </CustomerItem>
                </Row>
                <MoreEmpty />
              </React.Fragment>
            ) : (
              <React.Fragment>
                <MoreOverlay />
                <MoreGroup>
                  <MoreButton onClick={onMore}>
                    {translate(["customerSection", "moreText"], t)}
                    <MoreIcon
                      alt={"more"}
                      src={images.keyboardArrowDownSrc}
                      srcSet={images.keyboardArrowDown}
                    />
                  </MoreButton>
                </MoreGroup>
              </React.Fragment>
            )}
          </MediaQuery>
        </Content>
      </Inner>
    </Container>
  );
};

export default withTranslation()(CustomerSection);
